<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from "vue";
import Table from "../../../base-components/Table";
import Preview from "../../../base-components/Preview";
import { usePedidosStore } from "../../../services/pedidos";
import { userUserStore } from "../../../stores/user";
import { getFirstDayOfMonth } from "../../../utils/helper";
import { getCurrentDate } from "../../../utils/fecha";
import { storeToRefs } from "pinia";
import ExcelJS from "exceljs";
import {
  collection,
  getDocs,
  getFirestore,
  query,
  Timestamp,
  where
} from "firebase/firestore";
import LoadingIcon from "../../../base-components/LoadingIcon/LoadingIcon.vue";
import Lucide from "../../../base-components/Lucide/Lucide.vue";

const cargando = ref(false);
const pedidosStore = usePedidosStore();
const { filtros } = storeToRefs(pedidosStore);
const userStore = userUserStore();

watch(
  () => [filtros.value.fechaInicio, filtros.value.fechaFin],
  async ([inicio, fin]) => {
    if (!inicio || !fin) return;

    cargando.value = true;
    productosPorDia.value = [];
    await cargarProductos();
    cargando.value = false;
  }
);
const db = getFirestore();

const productosPorDia = ref<
  Array<{
    nombre: string;
    cantidad: number;
    idProducto: string;
    monto: number;
    descuento: number;
  }>
>([]);
const parseFecha = (fecha: string) => {
  const [dia, mes, anio] = fecha.split("/");
  return new Date(Number(anio), Number(mes) - 1, Number(dia));
};

const cargarProductos = async () => {
  const inicio = parseFecha(filtros.value.fechaInicio);
  const fin = parseFecha(filtros.value.fechaFin);

  // ajustar fin al final del día
  fin.setHours(23, 59, 59, 999);

  const q = query(
    collection(db, `empresas/${userStore.empresa?.id}/estadisticas-productos`),
    where("fecha", ">=", Timestamp.fromDate(inicio)),
    where("fecha", "<=", Timestamp.fromDate(fin))
  );

  const snap = await getDocs(q);

  const resultados: any[] = [];

  snap.forEach(doc => {
    resultados.push(doc.data());
  });

  productosPorDia.value = resultados;
};

const productosSumados = computed(() => {
  const mapa: Record<string, any> = {};

  for (const prod of productosPorDia.value) {
    const key = prod.idProducto || prod.nombre;

    if (!mapa[key]) {
      mapa[key] = {
        ...prod,
        cantidad: 0,
        monto: 0,
        descuento: 0
      };
    }

    mapa[key].cantidad += prod.cantidad;
    mapa[key].monto += prod.monto;
    mapa[key].descuento += prod.descuento || 0;
  }

  return Object.values(mapa).sort((a, b) => b.cantidad - a.cantidad);
});
onBeforeMount(async () => {
  filtros.value.fechaInicio = getFirstDayOfMonth();
  filtros.value.fechaFin = getCurrentDate();
  cargando.value = true;
  await cargarProductos();
  cargando.value = false;
});

const handleExport = async () => {
  const workbook = new ExcelJS.Workbook();

  const worksheet = workbook.addWorksheet("Hoja 1");
  const encabezados = [
    "Código",
    "Nombre",
    "Categoría",
    "Cantidad de ventas",
    "Monto ventas (incluye descuentos)",
    "Descuentos"
  ];
  worksheet.addRow(encabezados);

  for (const estadistica of productosSumados.value) {
    const datos = [
      estadistica.codigoProducto,
      estadistica.idProducto === "manual"
        ? "Producto manual"
        : estadistica.nombre,
      estadistica.rubro,
      estadistica.cantidad,
      estadistica.monto.toFixed(2),
      estadistica.descuento.toFixed(2)
    ];
    worksheet.addRow(datos);
  }

  workbook.xlsx.writeBuffer().then(buffer => {
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Estadísticas productos - ${filtros.value.fechaInicio}- ${filtros.value.fechaFin}.xlsx`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  });
};
</script>

<template>
  <Preview class="mt-4 box">
    <Preview.Panel>
      <div class="px-5 min-w-0 my-6">
        <h2 class="my-3 text-base ml-1">Reporte por productos</h2>
        <div class="overflow-y-visible min-w-0 -mx-5 px-5 grid">
          <Table bordered hover striped class="min-w-[700px]">
            <Table.Thead class="bg-[#E09C2D] text-white">
              <Table.Tr>
                <Table.Th class="whitespace-nowrap"> Código</Table.Th>
                <Table.Th class="whitespace-nowrap"> Nombre</Table.Th>
                <Table.Th class="whitespace-nowrap"> Categoría</Table.Th>
                <Table.Th class="whitespace-nowrap">
                  Cantidad de ventas</Table.Th
                >
                <Table.Th class="whitespace-nowrap">
                  Monto ventas (incluye descuentos)</Table.Th
                >
                <Table.Th class="whitespace-nowrap"> Descuentos </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <template
                v-for="estadistica in productosSumados"
                :key="estadistica.id"
              >
                <Table.Tr
                  class="[&:hover_td]:bg-[#E09C2D33]/5 [&:nth-of-type(odd)_td]:bg-[#E09C2D33]/20"
                >
                  <Table.Td> {{ estadistica.codigoProducto }} </Table.Td>
                  <Table.Td>
                    {{
                      estadistica.idProducto === "manual"
                        ? "Producto manual"
                        : estadistica.nombre
                    }}
                  </Table.Td>
                  <Table.Td> {{ estadistica.rubro }} </Table.Td>
                  <Table.Td>{{ estadistica.cantidad }} </Table.Td>
                  <Table.Td>{{ estadistica.monto.toFixed(2) }}</Table.Td>
                  <Table.Td>{{ estadistica.descuento.toFixed(2) }}</Table.Td>
                </Table.Tr>
              </template>
              <Table.Tr
                class="[&:hover_td]:bg-[#E09C2D33]/5 [&:nth-of-type(odd)_td]:bg-[#E09C2D33]/20"
                v-if="cargando"
              >
                <Table.Td colspan="6" class="py-8 text-slate-500">
                  <div class="flex justify-center">
                    <LoadingIcon icon="oval" class="w-8 h-8" />
                  </div>
                </Table.Td>
              </Table.Tr>
              <Table.Tr
                class="[&:hover_td]:bg-[#E09C2D33]/5 [&:nth-of-type(odd)_td]:bg-[#E09C2D33]/20"
                v-if="productosSumados.length === 0 && !cargando"
              >
                <Table.Td colspan="6" class="text-center py-8 text-slate-500">
                  No hay estadísticas para mostrar.
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </div>
        <div
          @click="handleExport"
          class="flex relative group mt-6 cursor-pointer ml-1"
        >
          <Lucide icon="Download" class="w-4 h-4 mr-2" />
          <p>Exportar datos a Excel</p>
        </div>
      </div>
    </Preview.Panel>
  </Preview>
</template>
